<template>
    <Transition name="fade">
        <div v-if="!isDismissed && !showAddedState" v-bind="$attrs">
            <BaseCard padding="none" radius="md" elevated class="overflow-hidden">
                <template v-if="isInactive || collaborationProfileShared">
                    <div v-if="isInactive" class="status-row bg-chip-neutral-bg text-ink-muted">
                        <i class="bi bi-slash-circle" aria-hidden="true"></i>
                        {{ t("chat.personCard.inactive") }}
                    </div>
                    <div v-else-if="collaborationProfileShared" class="status-row bg-chip-neutral-bg text-ink-muted" data-testid="collaboration-profile-chip">
                        <i class="bi bi-stars" aria-hidden="true"></i>
                        {{ t("chat.myPeople.collaborationProfile") }}
                    </div>
                </template>
                <BaseAccordion
                    :accordion-ids="['people']"
                    :header-class="isNewConnection ? 'hover:bg-transparent' : ''"
                    :trigger-class="isNewConnection ? 'px-5 py-5' : ''"
                    @update:model-value="onAccordionToggle">
                    <template #trigger-people>
                        <div class="flex items-center gap-3 grow text-left">
                            <PeopleAvatar :name="name" />
                            <div class="flex min-w-0 flex-col" :class="{ 'gap-1': !isNewConnection }">
                                <p class="nadia-chat-semibold leading-tight wrap-break-word">{{ name }}</p>
                                <p v-if="jobTitle" class="body-small" :class="{ 'text-ink-muted': isNewConnection }">{{ jobTitle }}</p>
                                <p v-else-if="isNewConnection" class="body-small text-ink-muted">{{ t("chat.personCard.roleNotKnown") }}</p>
                            </div>
                        </div>
                    </template>
                    <template v-if="isNewConnection" #expandIcon>
                        <!-- The accordion trigger already owns the button semantics. -->
                        <span class="base-btn-neutral base-btn-sm shrink-0">{{ addPersonLabel }}</span>
                    </template>
                    <template #content-people>
                        <div :class="{ 'border-t': !isNewConnection }">
                            <template v-if="isWrongPersonView">
                                <div :class="isNewConnection ? 'px-5 pb-5' : 'p-3'">
                                    <ChatConnectionDisambiguation
                                        :show-cancel="true"
                                        :allow-create="true"
                                        :exclude-connection-id="connectionUuid"
                                        :initial-search-term="isNewConnection ? name : ''"
                                        @select="handleWrongPersonSelect"
                                        @create="handleWrongPersonCreate"
                                        @cancel="isWrongPersonView = false" />
                                </div>
                            </template>
                            <div v-else-if="isNewConnection" class="px-5 pb-5">
                                <!-- Add someone new -->
                                <HorizontalDivider class="mt-0 mb-5" />
                                <label :for="nameInputId" class="flex flex-col gap-1">
                                    <span class="scaffold-details-small text-control-label">{{ t("chat.myPeople.nameLabel") }}</span>
                                    <BaseInput :id="nameInputId" v-model="form.name" :placeholder="t('chat.myPeople.namePlaceholder')" />
                                </label>
                                <label :for="roleInputId" class="mt-2.5 flex flex-col gap-1">
                                    <span class="scaffold-details-small text-control-label">{{ t("chat.myPeople.roleLabel") }}</span>
                                    <BaseInput :id="roleInputId" v-model="jobTitleInput" :placeholder="t('chat.myPeople.rolePlaceholder')" />
                                </label>
                                <BaseButton theme="primary" class="mt-5 flex w-full items-center justify-center gap-2" :disabled="!isFormValid" @click="addNewPerson">
                                    <i class="bi bi-plus-lg" aria-hidden="true"></i>
                                    {{ addPersonLabel }}
                                </BaseButton>
                                <HorizontalDivider class="my-4" />
                                <ChatMyPeopleAccordionFooter @wrong-person="handleWrongPerson" @decline="handleDecline" />
                            </div>
                            <div v-else class="p-3">
                                <p v-if="profilePulled" data-testid="profile-pulled-summary" class="body-small mb-2 text-ink-muted">
                                    <template v-if="!collaborationProfileShared">{{ profilePulledSummary }}</template>
                                    <I18nT v-else-if="hasNotes && hasDetails" keypath="chat.myPeople.profilePulled.withProfile.notesAndDetails" scope="global">
                                        <template #link><CollaborationProfileLink /></template>
                                    </I18nT>
                                    <I18nT v-else-if="hasNotes" keypath="chat.myPeople.profilePulled.withProfile.notes" scope="global">
                                        <template #link><CollaborationProfileLink /></template>
                                    </I18nT>
                                    <I18nT v-else-if="hasDetails" keypath="chat.myPeople.profilePulled.withProfile.details" scope="global">
                                        <template #link><CollaborationProfileLink /></template>
                                    </I18nT>
                                    <I18nT v-else keypath="chat.myPeople.profilePulled.withProfile.basic" scope="global">
                                        <template #link><CollaborationProfileLink /></template>
                                    </I18nT>
                                </p>
                                <p v-if="summary || !profilePulled" class="body-small mb-4">
                                    {{ summaryStr }}
                                </p>
                                <BaseButton theme="primary" size="sm" class="flex w-full items-center justify-center gap-2" @click="handleViewPerson">
                                    <i class="bi bi-box-arrow-up-right" aria-hidden="true"></i>
                                    {{ t("chat.myPeople.viewPerson") }}
                                </BaseButton>
                                <HorizontalDivider class="my-4" />
                                <ChatMyPeopleAccordionFooter @wrong-person="handleWrongPerson" @decline="handleDecline" />
                            </div>
                        </div>
                    </template>
                </BaseAccordion>
                <div v-if="canExploreCollaboration" class="border-t border-border p-3">
                    <BaseButton theme="tertiary" size="sm" class="w-full" :disabled="analysisBusy || analysisRequested" @click="exploreCollaboration">
                        <i class="bi bi-stars mr-2" aria-hidden="true"></i>
                        {{ analysisRequested ? t("chat.personCard.exploration.requested") : t("chat.personCard.exploration.explore") }}
                    </BaseButton>
                </div>
                <!-- Outside the accordion so linking no longer costs a chevron click, and collapsed-only
                     because the expanded view offers View Person as its primary action instead. -->
                <div v-if="showLinkSlot" class="border-t p-3">
                    <BaseButton
                        v-if="!isSearchOpen"
                        theme="tertiary"
                        size="sm"
                        class="flex w-full items-center justify-center gap-2 rounded-md border-2 border-dashed py-2"
                        data-testid="link-collaboration-profile"
                        @click="handleOpenSearch">
                        <i class="bi bi-plus-lg" aria-hidden="true"></i>
                        {{ t("chat.myPeople.linkCollaborationProfile") }}
                    </BaseButton>
                    <div v-else class="flex flex-col gap-2" data-testid="link-profile-search">
                        <div class="flex items-center gap-2">
                            <PublicProfileSearchCombobox
                                class="grow"
                                auto-focus
                                :initial-search-term="name"
                                :disabled="isLinking"
                                :actions="inviteActions"
                                @select="onLinkSelect"
                                @action="handleCopyInvite"
                                @update:query="searchQuery = $event"
                                @search-error="searchError = $event">
                                <template #no-match>
                                    <div class="flex gap-2 p-4 pb-2" data-testid="link-profile-no-match">
                                        <i class="bi bi-person-x text-ink-muted" aria-hidden="true"></i>
                                        <div class="flex min-w-0 flex-col gap-1">
                                            <p class="body-small-semibold">{{ noProfileMessage }}</p>
                                            <p class="body-small text-ink-muted">
                                                {{ t("chat.myPeople.noProfileHint") }}
                                            </p>
                                        </div>
                                    </div>
                                </template>
                                <template #action="{ action, hasNoMatch }">
                                    <!-- The combobox owns selection; this slot is presentation, not another button. -->
                                    <span
                                        :class="['flex items-center', hasNoMatch ? 'base-btn-primary base-btn-sm py-2 gap-2' : 'text-button-tertiary-text px-1 gap-3']"
                                        data-testid="invite-label">
                                        <span :class="['flex items-center justify-center', hasNoMatch ? 'text-lg/none' : 'border rounded-full size-8']">
                                            <i class="bi" :class="hasCopiedInvite ? 'bi-check-lg' : 'bi-link-45deg'" aria-hidden="true"></i>
                                        </span>
                                        <span>
                                            {{ inviteLabel(hasNoMatch) ?? action.label }}
                                        </span>
                                    </span>
                                </template>
                            </PublicProfileSearchCombobox>
                            <BaseButton theme="tertiary" size="sm" class="shrink-0" :disabled="isLinking" @click="handleCancelSearch"> {{ t("common.cancel") }} </BaseButton>
                        </div>
                        <BaseNotification v-if="searchError" theme="error" :text="t('chat.personCard.linkProfile.searchFailed')" @dismiss="searchError = null" />
                        <BaseNotification v-if="linkProfileError" theme="error" :text="t('chat.personCard.linkProfile.linkFailed')" @dismiss="linkProfileError = null" />
                        <BaseNotification v-if="inviteCopyFailed" theme="error" :text="t('chat.personCard.linkProfile.copyInviteFailed')" @dismiss="inviteCopyFailed = false" />
                    </div>
                </div>
            </BaseCard>
        </div>
        <div v-else-if="showAddedState" class="flex items-center gap-2.5 text-left rounded-lgr border border-border px-5 py-4">
            <PeopleAvatar :name="name" />
            <div class="flex min-w-0 flex-col">
                <p class="nadia-chat-semibold leading-tight wrap-break-word">{{ name }}</p>
                <p v-if="jobTitle" class="body-small text-ink-muted">{{ jobTitle }}</p>
                <p v-else class="body-small text-ink-muted">{{ t("chat.personCard.roleNotKnown") }}</p>
            </div>
            <StatusChip state="done" size="2xs" class="ml-auto shrink-0">
                <template #leading><i class="bi bi-check-lg" aria-hidden="true"></i></template>
                {{ t("chat.myPeople.added") }}
            </StatusChip>
        </div>
    </Transition>
</template>

<script setup lang="ts">
import { router } from "@inertiajs/vue3";
import type { GlobalProperties, OrganizationDetails } from "~vue/appTypes";
import { ChatAction, type PluckedAction } from "~vue/chatActions";
import { copyShareUrl, profileInviteMessage } from "~vue/CollaborationProfile/composables/useProfileShareActions";
import { logProfileShared, withShareAttribution } from "~vue/CollaborationProfile/profileShareAttribution";
import BaseAccordion from "~vue/components/BaseAccordion.vue";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import type { ComboboxAction } from "~vue/components/BaseCombobox.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BaseInput from "~vue/components/form/BaseInput.vue";
import HorizontalDivider from "~vue/components/HorizontalDivider.vue";
import PeopleAvatar from "~vue/components/PeopleAvatar.vue";
import StatusChip from "~vue/components/StatusChip.vue";
import { useLinkPublicProfile } from "~vue/composables/useLinkPublicProfile";
import { useTimedShow } from "~vue/composables/useTimedShow.js";
import PublicProfileSearchCombobox from "~vue/Connections/PublicProfileSearchCombobox.vue";
import { i18n } from "~vue/i18n";
import type { ActionState, UpdateActionState } from "~vue/types/chat";
import type { Connection, ConnectionProp } from "~vue/types/connection";
import type { ExistingPublicProfile } from "~vue/types/profile";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logError, logUserInteraction } from "~vue/utils/logUtils";
import { getFirstName } from "~vue/utils/stringUtils";
import { computed, type ComputedRef, type FunctionalComponent, h, inject, reactive, ref, useId, watch } from "vue";
import { I18nT } from "vue-i18n";

import ChatConnectionDisambiguation from "./ChatConnectionDisambiguation.vue";
import ChatMyPeopleAccordionFooter from "./ChatMyPeopleAccordionFooter.vue";

const { t } = i18n.global;

type ChatMyPeopleAccordionProps = {
    connectionChatMessageAction: PluckedAction<ChatAction.TAGGED_PERSON>;
    execute: (action: ChatAction, params: Record<string, unknown>, options?: Record<string, unknown>) => Promise<void>;
    csUuid?: string;
    connections?: ComputedRef<ConnectionProp[]>;
    shouldShowSubmitted?: boolean;
    publicProfileEnabled?: boolean;
    collaborationAnalysisEnabled?: boolean;
    collaborationAnalysisBusy?: ComputedRef<boolean>;
    collaborationExploredConnections?: ReadonlySet<string>;
    sendMessage?: (content: string, options?: { collaborationConnectionId: string }) => boolean;
    organizationDetails?: ComputedRef<OrganizationDetails | null>;
    collaborationProfileInviteUrl?: string;
    showToast?: (message: string) => void;
};

const {
    connectionChatMessageAction,
    execute,
    csUuid = "",
    connections = computed(() => []),
    shouldShowSubmitted = false,
    publicProfileEnabled = false,
    collaborationAnalysisEnabled = false,
    collaborationAnalysisBusy = computed(() => false),
    collaborationExploredConnections = new Set<string>(),
    sendMessage,
    organizationDetails: organizationDetailsProp = computed(() => null),
    collaborationProfileInviteUrl = "",
    showToast,
} = defineProps<ChatMyPeopleAccordionProps>();

// Each of these injects has a prop fallback because `useConnectionPopovers` mounts this card with a
// bare `render()`, which has no app context and so resolves every inject to its default.
const coachingSessionUuid = inject<string>("coachingSessionUuid", csUuid);
const allConnections = inject<ComputedRef<ConnectionProp[]>>("connections", connections);
const isPublicProfileEnabled = inject<boolean>("publicProfileEnabled", publicProfileEnabled);
const analysisEnabled = inject<boolean>("collaborationAnalysisEnabled", collaborationAnalysisEnabled);
const analysisBusy = inject<ComputedRef<boolean>>("collaborationAnalysisBusy", collaborationAnalysisBusy);
const exploredConnections = inject<ComputedRef<ReadonlySet<string>>>(
    "collaborationExploredConnections",
    computed(() => collaborationExploredConnections),
);
const sendChatMessage = inject<NonNullable<ChatMyPeopleAccordionProps["sendMessage"]>>("sendMessage", sendMessage ?? (() => false));
const analysisRequested = ref(false);
watch(analysisBusy, (busy, wasBusy) => {
    if (wasBusy && !busy) {
        analysisRequested.value = false;
    }
});
const organizationDetails = inject<ComputedRef<OrganizationDetails | null>>("organization_details", organizationDetailsProp);
const inviteUrl = inject<string>("collaborationProfileInviteUrl", collaborationProfileInviteUrl);
const showChatToast = inject<(message: string) => void>("showChatToast", showToast ?? (() => {}));

const updateActionState = inject<UpdateActionState>("updateActionState", () => {});
const { emitter } = inject<GlobalProperties>("globalProperties", {} as GlobalProperties);

const DEFAULT_ACTION_STATE: ActionState = { dismissed: false, minimized: false, submitted: false };

const revertActionState = (patch: Partial<ActionState>) => {
    updateActionState({
        message_id: connectionChatMessageAction.message.chat_message_id,
        lineIdx: connectionChatMessageAction.lineIdx,
        action_state: { ...DEFAULT_ACTION_STATE, ...connectionChatMessageAction.line.action_state, ...patch },
    });
};

const handleActionFailure = (error: unknown, action: string) => {
    logError(error, { context: { component: "ChatMyPeopleAccordion", action }, tags: { error_type: "tagged_person_action" } });
    emitter.emit("error_notification", t("chat.personCard.actionFailed"));
};

const nameInputId = useId();
const roleInputId = useId();

const connectionUuid = computed(() => connectionChatMessageAction.line.action_params.connection);
const profileHref = computed(() => `/people/${connectionUuid.value}/`);
const currentConnection = computed(() => allConnections.value.find((conn) => conn.id === connectionUuid.value));
const name = computed(() => currentConnection.value?.name ?? connectionChatMessageAction.line.action_params.name);
const jobTitle = computed(() => currentConnection.value?.job_title ?? connectionChatMessageAction.line.action_params.title);
const isInactive = computed(() => currentConnection.value?.is_inactive ?? false);
const summary = computed(() => currentConnection.value?.summary);
const summaryStr = computed(() => summary.value ?? t("chat.myPeople.summaryUnavailable"));

const profilePulled = computed(() => connectionChatMessageAction.line.action_params.profile_pulled ?? false);
const collaborationProfileShared = computed(
    () => currentConnection.value?.collaboration_profile_shared ?? connectionChatMessageAction.line.action_params.collaboration_profile_shared ?? false,
);
const canExploreCollaboration = computed(
    () =>
        analysisEnabled &&
        !!currentConnection.value &&
        !currentConnection.value.declined_at &&
        currentConnection.value.collaboration_analysis_available === true &&
        !isInactive.value &&
        !isNewConnection.value &&
        !exploredConnections.value.has(connectionChatMessageAction.line.action_params.connection),
);
const exploreCollaboration = () => {
    if (!canExploreCollaboration.value || analysisBusy.value || analysisRequested.value) {
        return;
    }
    analysisRequested.value = sendChatMessage(t("chat.personCard.exploration.message", { name: name.value }), { collaborationConnectionId: connectionUuid.value });
};
const hasNotes = computed(() => connectionChatMessageAction.line.action_params.has_notes ?? false);
const hasDetails = computed(() => connectionChatMessageAction.line.action_params.has_details ?? false);
const profilePulledSummary = computed(() => {
    if (hasNotes.value && hasDetails.value) {
        return t("chat.myPeople.profilePulled.notesAndDetails");
    }
    if (hasNotes.value) {
        return t("chat.myPeople.profilePulled.notes");
    }
    if (hasDetails.value) {
        return t("chat.myPeople.profilePulled.details");
    }
    return t("chat.myPeople.profilePulled.basic");
});

const isSubmitted = computed(() => connectionChatMessageAction.line.action_state?.submitted);
const isNewConnection = computed(() => {
    const params = connectionChatMessageAction.line.action_params;
    return !isSubmitted.value && params.was_created && (params.name || params.title || params.relationship);
});

const handleWrongPersonSelect = async (conn: Connection) => {
    isWrongPersonView.value = false;
    try {
        await execute(
            ChatAction.TAGGED_PERSON,
            {
                message_id: connectionChatMessageAction.message.chat_message_id,
                lineIdx: connectionChatMessageAction.lineIdx,
                index: connectionChatMessageAction.line.index,
                subaction: "reassign",
                correct_connection_id: conn.id,
                // Hide the card straight away rather than waiting on the response: `streaming: true`
                // only resolves once the whole assistant stream has finished. The server dismisses
                // this same action, and the picked person is confirmed by the tag it attaches to the
                // injected correction message.
                action_state: { dismissed: true },
            },
            { streaming: true },
        );
        logUserInteraction(AmplitudeEventNames.CONNECTION_DISAMBIGUATED, { connection_id: conn.id }, coachingSessionUuid);
    } catch (error) {
        revertActionState({ dismissed: false });
        handleActionFailure(error, "reassign");
    } finally {
        router.reload({ only: ["connections"] });
    }
};

const handleWrongPersonCreate = async (personName: string) => {
    isWrongPersonView.value = false;
    try {
        await execute(
            ChatAction.TAGGED_PERSON,
            {
                message_id: connectionChatMessageAction.message.chat_message_id,
                lineIdx: connectionChatMessageAction.lineIdx,
                index: connectionChatMessageAction.line.index,
                // Hide this card straight away rather than waiting on the response: `streaming: true`
                // only resolves once the whole assistant stream has finished. The server dismisses
                // this same action, and the added person is confirmed by the tag it attaches to the
                // injected correction message.
                action_state: { dismissed: true },
                subaction: "reassign_new",
                action_params: { name: personName },
            },
            { streaming: true },
        );
        logUserInteraction(AmplitudeEventNames.CONNECTION_DISAMBIGUATED, { source: "wrong_person_add_new", connection_id: connectionUuid.value }, coachingSessionUuid);
    } catch (error) {
        revertActionState({ dismissed: false });
        handleActionFailure(error, "reassign_new");
    } finally {
        router.reload({ only: ["connections"] });
    }
};

const form = reactive({
    name: name.value,
    jobTitle: jobTitle.value,
});
const jobTitleInput = computed({
    get: () => form.jobTitle ?? "",
    set: (value: string) => {
        form.jobTitle = value;
    },
});

const addPersonLabel = computed(() => {
    const firstName = getFirstName(form.name || name.value);
    return firstName ? t("chat.myPeople.addPerson", { name: firstName }) : t("chat.myPeople.addThisPerson");
});

const showAddedState = computed(() => shouldShowSubmitted && isSubmitted.value);

const isFormValid = computed(() => !!form.name?.trim());

const addNewPerson = async () => {
    if (!isFormValid.value) {
        return;
    }

    try {
        await execute(ChatAction.TAGGED_PERSON, {
            message_id: connectionChatMessageAction.message.chat_message_id,
            lineIdx: connectionChatMessageAction.lineIdx,
            index: connectionChatMessageAction.line.index,
            action_state: { submitted: true },
            action_params: {
                name: form.name,
                title: form.jobTitle,
            },
            subaction: "submit",
        });
        logUserInteraction(AmplitudeEventNames.CONNECTION_UPDATE, { source: "chat", connection_id: connectionUuid.value }, coachingSessionUuid);
    } catch (error) {
        revertActionState({ submitted: false });
        handleActionFailure(error, "submit");
    } finally {
        router.reload({ only: ["connections"] });
    }
};

const isProfileLinked = computed(() => !!currentConnection.value?.public_profile);
const isExpanded = ref(false);
const isSearchOpen = ref(false);
const searchError = ref<unknown>(null);

const inviteCopyFailed = ref(false);
const searchQuery = ref("");

const searchedName = computed(() => searchQuery.value.trim() || name.value);
const inviteeFirstName = computed(() => getFirstName(searchedName.value));
// profileInviteMessage owns its (English) sentence; this fallback keeps its existing wording.
const inviteFirstName = computed(() => inviteeFirstName.value || "them");

const noProfileMessage = computed(() => {
    const org = organizationDetails.value;
    return !org || org.is_default || !org.name
        ? t("chat.myPeople.noProfileAtYourCompany", { name: searchedName.value })
        : t("chat.myPeople.noProfile", { name: searchedName.value, organization: org.name });
});

// The link affordance belongs to the collapsed card only; expanding swaps in the notes context, whose
// primary action is View Person.
const showLinkSlot = computed(() => isPublicProfileEnabled && !isProfileLinked.value && !isNewConnection.value && !isExpanded.value);

const { show: showCopied, isShowing: hasCopiedInvite } = useTimedShow();
const inviteActions = computed<ComboboxAction[]>(() => [{ id: "copy-invite", label: t("chat.personCard.linkProfile.copyInviteLink"), closeOnSelect: false }]);

const inviteLabel = (hasNoMatch: boolean) => {
    if (hasCopiedInvite.value) {
        return t("chat.personCard.linkProfile.copied");
    }
    return hasNoMatch ? t("chat.personCard.linkProfile.copyInviteLink") : t("chat.myPeople.notHereCopyInvite");
};

const handleCopyInvite = async (id: ComboboxAction["id"]) => {
    if (id !== inviteActions.value[0].id) {
        return;
    }

    inviteCopyFailed.value = false;
    if (!(await copyShareUrl(withShareAttribution(inviteUrl, "chat", "link"), profileInviteMessage(inviteFirstName.value)))) {
        inviteCopyFailed.value = true;
        return;
    }
    showCopied();
    showChatToast(inviteeFirstName.value ? t("chat.personCard.linkProfile.copiedToast", { name: inviteeFirstName.value }) : t("chat.personCard.linkProfile.copiedToastNoName"));
    logUserInteraction(AmplitudeEventNames.CONNECTION_INVITE_COPIED, { connection_id: connectionUuid.value, source: "chat" }, coachingSessionUuid);
    void logProfileShared({ shareSource: "chat", target: "link", shareKind: "invite", connectionId: connectionUuid.value }, coachingSessionUuid);
};

const handleCancelSearch = () => {
    isSearchOpen.value = false;
    logUserInteraction(
        AmplitudeEventNames.CONNECTION_LINK_SEARCH_CANCELED,
        { connection_id: connectionUuid.value, copied_invite: hasCopiedInvite.value, source: "chat" },
        coachingSessionUuid,
    );
};

const {
    linkProfile,
    isLinking,
    error: linkProfileError,
} = useLinkPublicProfile({
    connectionId: connectionUuid,
    source: "chat",
    coachingSessionUuid,
    onLinked: () => {
        router.reload({
            only: ["connections"],
            onSuccess: () => {
                isSearchOpen.value = false;
            },
        });
    },
});

const handleOpenSearch = () => {
    logUserInteraction(AmplitudeEventNames.CONNECTION_LINK_PROFILE_BUTTON_CLICKED, { connection_id: connectionUuid.value, source: "chat" }, coachingSessionUuid);
    isSearchOpen.value = true;
    inviteCopyFailed.value = false;
};

const onLinkSelect = (profile: ExistingPublicProfile) => {
    linkProfile(profile.user);
};

const handleViewPerson = () => {
    if (connectionUuid.value) {
        logUserInteraction(AmplitudeEventNames.CONNECTION_VIEW_PERSON_CLICKED, { connection_id: connectionUuid.value }, coachingSessionUuid);
        globalThis.open(profileHref.value, "_blank");
    }
};

const logCollaborationProfileClick = () => {
    if (connectionUuid.value) {
        logUserInteraction(AmplitudeEventNames.CONNECTION_COLLABORATION_PROFILE_CLICKED, { connection_id: connectionUuid.value }, coachingSessionUuid);
    }
};

const CollaborationProfileLink: FunctionalComponent = () =>
    h(
        "a",
        {
            href: profileHref.value,
            target: "_blank",
            rel: "noopener",
            "data-testid": "collaboration-profile-link",
            class: "base-link",
            onClick: logCollaborationProfileClick,
        },
        t("chat.myPeople.profilePulled.linkedProfile"),
    );

const onAccordionToggle = (openIds: string[]) => {
    isExpanded.value = openIds.includes("people");
    if (isExpanded.value) {
        // Expanding hides the link slot, so drop any in-progress search rather than silently restoring
        // a stale query when the card is collapsed again.
        isSearchOpen.value = false;
        if (connectionUuid.value) {
            logUserInteraction(AmplitudeEventNames.CONNECTION_CARD_VIEWED, { connection_id: connectionUuid.value }, coachingSessionUuid);
        }
    }
};

const isWrongPersonView = ref(false);
const handleWrongPerson = () => {
    isWrongPersonView.value = true;
};

const handleDecline = async () => {
    try {
        await execute(ChatAction.TAGGED_PERSON, {
            message_id: connectionChatMessageAction.message.chat_message_id,
            lineIdx: connectionChatMessageAction.lineIdx,
            index: connectionChatMessageAction.line.index,
            action_state: { dismissed: true },
            subaction: "dismiss",
        });
        logUserInteraction(AmplitudeEventNames.CONNECTION_DISMISSED, { source: "decline_button", connection_id: connectionUuid.value }, coachingSessionUuid);
    } catch (error) {
        revertActionState({ dismissed: false });
        handleActionFailure(error, "dismiss");
    } finally {
        router.reload({ only: ["connections"] });
    }
};

const isDismissed = computed(() => connectionChatMessageAction.line.action_state?.dismissed);
</script>

<style scoped>
@reference "../../../css/tailwind.css";

.status-row {
    @apply scaffold-details-small flex items-center gap-1 whitespace-nowrap px-3 py-2 border-b;
}
</style>
